import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import IconButton from "@mui/material/IconButton";
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { AppDateField } from "../components/form";
import { formatRangeDateForApi, parseProductionVariantSearchResponse } from "../helpers/utils";

const API_PROD = "/api/production";
const API_WS = "/api/workshop-payroll";

/**
 * Діагностика автокомпліту варіантів (`GET /variants?debug=1` + лог у браузері).
 * Увімкнення: `localStorage.setItem('VARIANT_SEARCH_DEBUG','1')` або додати в URL `?variantDebug=1`, перезавантажити сторінку.
 * У консолі браузера з’явиться `_variantServerDebug` — етапи по ProductW (що зникло на кожному кроці).
 */
function isVariantSearchDebugEnabled() {
    if (typeof window === "undefined") {
        return false;
    }
    try {
        if (localStorage.getItem("VARIANT_SEARCH_DEBUG") === "1") {
            return true;
        }
    } catch {
        /* private mode */
    }
    try {
        return new URLSearchParams(window.location.search).get("variantDebug") === "1";
    } catch {
        return false;
    }
}

/** `YYYY-MM-DD` → ISO для API: локальний полудень обраного дня (без зсуву дати через UTC). */
function dateYmdToIsoNoon(ymd) {
    if (!ymd || typeof ymd !== "string") return undefined;
    const [y, m, da] = ymd.split("-").map(Number);
    if (!y || !m || !da) return undefined;
    return new Date(y, m - 1, da, 12, 0, 0, 0).toISOString();
}

function newRow() {
    return {
        key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        offerId: null,
        display: "",
        thumbnailUrl: "",
        search: "",
        suggestions: [],
        qty: 1,
        searching: false,
    };
}

/** Мініатюра варіанта з CRM (`thumbnailUrl`), як у редакторі техкарти. */
function VariantThumb({ url, sizePx = 36 }) {
    const s = `${sizePx}px`;
    if (url) {
        return (
            <img
                src={url}
                alt=""
                className="img-thumbnail production-deliveries-variant-thumb p-0 flex-shrink-0"
                style={{ width: s, height: s, objectFit: "contain" }}
                loading="lazy"
            />
        );
    }
    return (
        <div
            className="img-thumbnail production-deliveries-variant-thumb-placeholder flex-shrink-0 d-flex align-items-center justify-content-center text-muted user-select-none"
            style={{ width: s, height: s, fontSize: "0.65rem" }}
            aria-hidden
        >
            —
        </div>
    );
}

function deliveryLinesToEditRows(lines) {
    const arr = Array.isArray(lines) ? lines : [];
    if (arr.length === 0) {
        return [newRow()];
    }
    return arr.map((L) => ({
        key: L._id ? String(L._id) : `ed-${L.offerId}-${Math.random().toString(36).slice(2)}`,
        offerId: L.offerId,
        display: `${[L.name, L.colorValue].filter(Boolean).join(" · ")}${L.variantLabel ? ` (${L.variantLabel})` : ""}`.trim() || `#${L.offerId}`,
        thumbnailUrl: String(L.thumbnailUrl || "").trim(),
        search: "",
        suggestions: [],
        qty: L.quantity,
        searching: false,
    }));
}

/**
 * Пошук варіанта: у модалці список підказок у portal + fixed, щоб не обрізався `overflow` батьків.
 */
function VariantSearchCell({
    row: r,
    compactHeading,
    listZIndex,
    inModal,
    onSearchChange,
    onPickSuggestion,
    onClearOffer,
}) {
    const wrapRef = useRef(null);
    const [portalBox, setPortalBox] = useState(null);

    useLayoutEffect(() => {
        if (!inModal || !r.suggestions?.length || !wrapRef.current) {
            setPortalBox(null);
            return;
        }
        const anchor = wrapRef.current;
        const update = () => {
            const rect = anchor.getBoundingClientRect();
            const margin = 6;
            const maxList = 240;
            const spaceBelow = window.innerHeight - rect.bottom - margin;
            const spaceAbove = rect.top - margin;
            const openDown = spaceBelow >= spaceAbove;
            if (openDown) {
                const maxH = Math.min(maxList, Math.max(80, spaceBelow));
                setPortalBox({
                    mode: "down",
                    left: rect.left,
                    width: rect.width,
                    top: rect.bottom + 2,
                    maxHeight: maxH,
                });
            } else {
                const maxH = Math.min(maxList, Math.max(80, spaceAbove));
                setPortalBox({
                    mode: "up",
                    left: rect.left,
                    width: rect.width,
                    maxHeight: maxH,
                    bottom: window.innerHeight - rect.top + 2,
                });
            }
        };
        update();
        window.addEventListener("resize", update);
        document.addEventListener("scroll", update, true);
        return () => {
            window.removeEventListener("resize", update);
            document.removeEventListener("scroll", update, true);
        };
    }, [inModal, r.suggestions, r.key, r.search, r.searching]);

    const listInner = r.suggestions.map((s) => (
        <button
            key={s.offerId}
            type="button"
            className="list-group-item list-group-item-action py-1 small text-start d-flex align-items-center gap-2 production-deliveries-variant-suggest-item"
            onClick={() => onPickSuggestion(s)}
        >
            <VariantThumb url={String(s.thumbnailUrl || "").trim()} sizePx={36} />
            <span className="min-w-0 text-break">{s.display}</span>
        </button>
    ));

    const listAbsolute = (
        <div
            className="list-group position-absolute shadow-sm mt-1 border bg-white"
            style={{
                zIndex: listZIndex,
                maxHeight: 220,
                overflowY: "auto",
                left: 0,
                right: 0,
                top: "100%",
            }}
        >
            {listInner}
        </div>
    );

    const listPortal =
        portalBox &&
        createPortal(
            <div
                className="list-group shadow-sm border bg-white production-deliveries-variant-suggest-portal"
                style={{
                    position: "fixed",
                    zIndex: Math.max(Number(listZIndex) || 1060, 2000),
                    left: portalBox.left,
                    width: portalBox.width,
                    maxHeight: portalBox.maxHeight,
                    overflowY: "auto",
                    ...(portalBox.mode === "down"
                        ? { top: portalBox.top, bottom: "auto" }
                        : { top: "auto", bottom: portalBox.bottom }),
                }}
                role="listbox"
            >
                {listInner}
            </div>,
            document.body
        );

    return (
        <div ref={wrapRef} className="position-relative">
            {r.offerId ? (
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <VariantThumb url={String(r.thumbnailUrl || "").trim()} sizePx={compactHeading ? 32 : 36} />
                    <span className={compactHeading ? "small fw-medium" : "fw-medium"}>{r.display}</span>
                    <Button type="button" variant="outline-secondary" size="sm" onClick={() => onClearOffer()}>
                        Змінити
                    </Button>
                </div>
            ) : (
                <>
                    <Form.Control
                        size={compactHeading ? "sm" : undefined}
                        value={r.search}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder="SKU, назва, властивості…"
                        autoComplete="off"
                        aria-label="Пошук варіанту товару"
                    />
                    {r.searching ? <small className="text-muted d-block mt-1">Пошук…</small> : null}
                    {r.suggestions.length > 0
                        ? inModal
                            ? portalBox
                                ? listPortal
                                : listAbsolute
                            : listAbsolute
                        : null}
                </>
            )}
        </div>
    );
}

/**
 * Рядки поставки — таблиця як у редакторі техкарти (`tech-card-materials-editor-table`).
 */
function DeliveryLinesEditorTable({
    rows,
    onPickVariant,
    onClearOffer,
    onSearchChange,
    onQtyChange,
    onRemoveRow,
    onAddRow,
    compactHeading,
    listZIndex = 10,
    /** У модалці: таблиця росте з рядками; після ліміту висоти — лише вертикальний скрол у цьому блоці. */
    inModal = false,
}) {
    const linesScrollRef = useRef(null);
    const prevRowCountRef = useRef(rows.length);

    useLayoutEffect(() => {
        if (!inModal) {
            prevRowCountRef.current = rows.length;
            return;
        }
        const el = linesScrollRef.current;
        if (!el) {
            prevRowCountRef.current = rows.length;
            return;
        }
        const prev = prevRowCountRef.current;
        if (rows.length > prev) {
            el.scrollTop = el.scrollHeight;
            requestAnimationFrame(() => {
                el.scrollTop = el.scrollHeight;
            });
        }
        prevRowCountRef.current = rows.length;
    }, [rows.length, inModal]);

    const tableBlock = (
        <div
            className={
                inModal
                    ? "table-responsive border rounded production-deliveries-lines-wrap"
                    : "table-responsive border rounded mb-2 production-deliveries-lines-wrap"
            }
        >
            <Table
                bordered
                hover
                size="sm"
                className="align-middle mb-0 tech-card-materials-editor-table production-deliveries-lines-table"
            >
                    <caption className="visually-hidden">Позиції поставки зі складу</caption>
                    <thead className="table-light">
                        <tr>
                            <th className="text-center text-nowrap align-middle" style={{ width: 40 }}>
                                #
                            </th>
                            <th className="text-start align-middle">Варіант</th>
                            <th className="text-nowrap text-end align-middle tech-card-th-qty">
                                Кількість, шт
                            </th>
                            <th className="text-nowrap text-center align-middle tech-card-th-actions">
                                Дії
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, rowIndex) => (
                            <tr key={r.key}>
                                <td className="text-center text-muted small py-2 align-middle">
                                    {rowIndex + 1}
                                </td>
                                <td className="py-2 text-start align-middle production-deliveries-td-variant">
                                    <VariantSearchCell
                                        row={r}
                                        compactHeading={compactHeading}
                                        listZIndex={listZIndex}
                                        inModal={inModal}
                                        onSearchChange={(v) => onSearchChange(r.key, v)}
                                        onPickSuggestion={(s) => onPickVariant(r.key, s)}
                                        onClearOffer={() => onClearOffer(r.key)}
                                    />
                                </td>
                                <td className="py-2 text-end align-middle tech-card-td-qty production-deliveries-td-qty">
                                    <Form.Control
                                        type="number"
                                        min={1}
                                        size={compactHeading ? "sm" : undefined}
                                        value={r.qty}
                                        onChange={(e) => onQtyChange(r.key, e.target.value)}
                                        aria-label="Кількість, шт"
                                        className="production-deliveries-qty-input"
                                    />
                                </td>
                                <td className="py-2 text-center align-middle tech-card-td-actions">
                                    <IconButton
                                        type="button"
                                        size="small"
                                        title="Прибрати рядок"
                                        aria-label="Прибрати рядок"
                                        onClick={() => onRemoveRow(r.key)}
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            padding: 0.5,
                                            color: "text.secondary",
                                            "&:hover": {
                                                color: "error.main",
                                                backgroundColor: "action.hover",
                                            },
                                        }}
                                    >
                                        <DeleteOutline fontSize="small" />
                                    </IconButton>
                                </td>
                            </tr>
                        ))}
                    </tbody>
            </Table>
        </div>
    );

    return (
        <>
            {inModal ? (
                <div className="production-deliveries-lines-modal-editor">
                    <div ref={linesScrollRef} className="production-deliveries-lines-modal-scroll">
                        {tableBlock}
                    </div>
                    <div className="production-deliveries-lines-modal-add-row d-flex justify-content-end">
                        <Button
                            type="button"
                            variant="outline-primary"
                            className="px-3"
                            onClick={onAddRow}
                            aria-label="Додати рядок"
                        >
                            +
                        </Button>
                    </div>
                </div>
            ) : (
                <>
                    {tableBlock}
                    <div className="mb-2">
                        <Button
                            type="button"
                            variant="outline-primary"
                            className="px-3"
                            onClick={onAddRow}
                            aria-label="Додати рядок"
                        >
                            +
                        </Button>
                    </div>
                </>
            )}
        </>
    );
}

function formatQtyHundredths(n) {
    const x = Number(n);
    if (!Number.isFinite(x)) {
        return "—";
    }
    return x.toLocaleString("uk-UA", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
}

/** Текст для пошуку по поставці (дата, цех, рядки, списання). */
function deliverySearchBlob(d) {
    const parts = [];
    if (d.deliveredAt) {
        parts.push(new Date(d.deliveredAt).toLocaleDateString("uk-UA"));
    }
    parts.push(String(d.note || ""));
    parts.push(d.workshopId?.name || "", d.sourceLabel || "");
    if (d.skipMaterialWriteoffs) {
        parts.push("без списання матеріалів");
    }
    (d.lines || []).forEach((L) => {
        parts.push(
            L.name,
            L.colorValue,
            L.variantLabel,
            String(L.quantity),
            String(L.offerId || "")
        );
    });
    (d.materialWriteoffs || []).forEach((m) => {
        parts.push(m.typeName, m.color, String(m.qty), m.unit);
    });
    parts.push(String(d.totalPieces ?? ""));
    return parts
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
}

/** Усі токени (через пробіл) мають входити в текст поставки — як зручний «мульти»-фільтр. */
function deliveryMatchesListFilter(d, qRaw) {
    const tokens = String(qRaw || "")
        .toLowerCase()
        .split(/\s+/)
        .map((t) => t.trim())
        .filter(Boolean);
    if (tokens.length === 0) {
        return true;
    }
    const blob = deliverySearchBlob(d);
    return tokens.every((t) => blob.includes(t));
}

/** Початок календарного тижня (понеділок 00:00:00, локальний час). */
function startOfLocalMonday(d) {
    const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const day = x.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    x.setDate(x.getDate() + diff);
    x.setHours(0, 0, 0, 0);
    return x;
}

function addDays(d, n) {
    const x = new Date(d.getTime());
    x.setDate(x.getDate() + n);
    return x;
}

function endOfLocalWeekSundayEnd(mondayStart) {
    const e = new Date(mondayStart.getFullYear(), mondayStart.getMonth(), mondayStart.getDate() + 6);
    e.setHours(23, 59, 59, 999);
    return e;
}

function isDeliveryInWeek(d, weekStartMonday) {
    if (!d?.deliveredAt) {
        return false;
    }
    const t = new Date(d.deliveredAt).getTime();
    const a = weekStartMonday.getTime();
    const b = endOfLocalWeekSundayEnd(weekStartMonday).getTime();
    return t >= a && t <= b;
}

/**
 * Перший показаний тиждень: поточний, якщо є хоча б одна поставка; інакше — попередній календарний тиждень.
 * Якщо поставок немає взагалі — поточний тиждень (порожня таблиця).
 */
function computeInitialWeekStartMonday(deliveries) {
    const cur = startOfLocalMonday(new Date());
    const list = Array.isArray(deliveries) ? deliveries : [];
    if (list.length === 0) {
        return cur;
    }
    const hasCurrent = list.some((d) => isDeliveryInWeek(d, cur));
    if (hasCurrent) {
        return cur;
    }
    return addDays(cur, -7);
}

function formatWeekRangeUk(weekStartMonday) {
    const sun = addDays(weekStartMonday, 6);
    const opts = { day: "numeric", month: "long", year: "numeric" };
    return `${weekStartMonday.toLocaleDateString("uk-UA", opts)} — ${sun.toLocaleDateString("uk-UA", opts)}`;
}

export default function ProductionDeliveriesPage() {
    const [workshops, setWorkshops] = useState([]);
    const [deliveries, setDeliveries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [deliveredAt, setDeliveredAt] = useState(() => formatRangeDateForApi(new Date()));
    const [workshopId, setWorkshopId] = useState("");
    const [note, setNote] = useState("");
    const [skipMaterialWriteoffs, setSkipMaterialWriteoffs] = useState(false);
    const [rows, setRows] = useState([newRow()]);

    const [editId, setEditId] = useState(null);
    const [editLocked, setEditLocked] = useState(false);
    const [editWorkshopId, setEditWorkshopId] = useState("");
    const [editDeliveredAt, setEditDeliveredAt] = useState("");
    const [editNote, setEditNote] = useState("");
    const [editSkip, setEditSkip] = useState(false);
    const [editRows, setEditRows] = useState([newRow()]);
    const [savingEdit, setSavingEdit] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const [filterQ, setFilterQ] = useState("");
    const [createModalOpen, setCreateModalOpen] = useState(false);

    /** Календарний тиждень (пн–нд) для списку поставок; після першого завантаження — за правилом поточний / попередній. */
    const [listWeekStartMonday, setListWeekStartMonday] = useState(() =>
        computeInitialWeekStartMonday([])
    );
    const deliveriesWeekInitDone = useRef(false);

    const loadAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [dRes, wRes] = await Promise.allSettled([
                axios.get(`${API_PROD}/deliveries`, { withCredentials: true }),
                axios.get(`${API_WS}/workshops`, { withCredentials: true }),
            ]);
            if (dRes.status === "fulfilled") {
                setDeliveries(dRes.value.data || []);
            } else {
                const msg =
                    dRes.reason?.response?.data?.error ||
                    dRes.reason?.message ||
                    "Не вдалося завантажити поставки";
                setError(msg);
                setDeliveries([]);
            }
            if (wRes.status === "fulfilled") {
                setWorkshops(wRes.value.data || []);
            } else {
                const msg =
                    wRes.reason?.response?.data?.error ||
                    wRes.reason?.message ||
                    "Не вдалося завантажити цехи";
                setError((prev) => (prev ? `${prev}; ${msg}` : msg));
                setWorkshops([]);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    useEffect(() => {
        if (loading) {
            return;
        }
        if (deliveriesWeekInitDone.current) {
            return;
        }
        deliveriesWeekInitDone.current = true;
        setListWeekStartMonday(computeInitialWeekStartMonday(deliveries));
    }, [loading, deliveries]);

    const resetCreateForm = useCallback(() => {
        setDeliveredAt(formatRangeDateForApi(new Date()));
        setWorkshopId("");
        setNote("");
        setSkipMaterialWriteoffs(false);
        setRows([newRow()]);
    }, []);

    const openCreateModal = useCallback(() => {
        resetCreateForm();
        setCreateModalOpen(true);
    }, [resetCreateForm]);

    const filteredDeliveries = useMemo(
        () => (deliveries || []).filter((d) => deliveryMatchesListFilter(d, filterQ)),
        [deliveries, filterQ]
    );

    const deliveriesInSelectedWeek = useMemo(
        () => filteredDeliveries.filter((d) => isDeliveryInWeek(d, listWeekStartMonday)),
        [filteredDeliveries, listWeekStartMonday]
    );

    /** Накат — це друк на готових речах, не відшиті вироби: у загальну кількість за період не входить. */
    const isNakatDelivery = (d) =>
        String(d.workshopId?.name || d.sourceLabel || "")
            .trim()
            .toLowerCase() === "накат";

    const weekTotalPieces = useMemo(
        () =>
            deliveriesInSelectedWeek.reduce((sum, d) => {
                if (isNakatDelivery(d)) {
                    return sum;
                }
                const n = Number(d.totalPieces);
                return sum + (Number.isFinite(n) ? n : 0);
            }, 0),
        [deliveriesInSelectedWeek]
    );

    const weekNakatPieces = useMemo(
        () =>
            deliveriesInSelectedWeek.reduce((sum, d) => {
                if (!isNakatDelivery(d)) {
                    return sum;
                }
                const n = Number(d.totalPieces);
                return sum + (Number.isFinite(n) ? n : 0);
            }, 0),
        [deliveriesInSelectedWeek]
    );

    const canGoNewerDeliveryWeek =
        listWeekStartMonday.getTime() < startOfLocalMonday(new Date()).getTime();

    const goOlderDeliveryWeek = useCallback(() => {
        setListWeekStartMonday((w) => addDays(w, -7));
    }, []);

    const goNewerDeliveryWeek = useCallback(() => {
        setListWeekStartMonday((w) => {
            const cap = startOfLocalMonday(new Date());
            const next = addDays(w, 7);
            if (next.getTime() > cap.getTime()) {
                return w;
            }
            return next;
        });
    }, []);

    const fetchSuggestions = async (key, q) => {
        if (!q || q.trim().length < 1) {
            setRows((prev) =>
                prev.map((r) => (r.key === key ? { ...r, suggestions: [], searching: false } : r))
            );
            return;
        }
        setRows((prev) =>
            prev.map((r) => (r.key === key ? { ...r, searching: true } : r))
        );
        try {
            const variantDbg = isVariantSearchDebugEnabled();
            const res = await axios.get(`${API_PROD}/variants`, {
                params: {
                    q: q.trim(),
                    include_sewing_excluded: "1",
                    ...(variantDbg ? { debug: "1" } : {}),
                },
                withCredentials: true,
            });
            const { offers, serverDebug } = parseProductionVariantSearchResponse(res.data);
            if (variantDbg) {
                console.info("[VARIANT_SEARCH_DEBUG client]", {
                    context: "create-delivery",
                    q: q.trim(),
                    returnedCount: offers.length,
                    offerIds: offers.map((x) => x.offerId),
                    skus: offers.map((x) => x.sku),
                });
                if (serverDebug) {
                    console.info("[VARIANT_SEARCH_DEBUG server → ProductW stages]", serverDebug);
                }
            }
            setRows((prev) =>
                prev.map((r) =>
                    r.key === key ? { ...r, suggestions: offers, searching: false } : r
                )
            );
        } catch {
            setRows((prev) =>
                prev.map((r) => (r.key === key ? { ...r, suggestions: [], searching: false } : r))
            );
        }
    };

    useEffect(() => {
        if (!createModalOpen) {
            return;
        }
        const timers = {};
        rows.forEach((r) => {
            if (r.offerId) return;
            const q = String(r.search || "").trim();
            if (q.length < 1) return;
            timers[r.key] = setTimeout(() => fetchSuggestions(r.key, q), 320);
        });
        return () => {
            Object.values(timers).forEach(clearTimeout);
        };
    }, [createModalOpen, rows.map((r) => `${r.key}:${r.search}`).join("|")]);

    const pickVariant = (key, v) => {
        setRows((prev) =>
            prev.map((r) =>
                r.key === key
                    ? {
                          ...r,
                          offerId: v.offerId,
                          display: v.display,
                          thumbnailUrl: String(v.thumbnailUrl || "").trim(),
                          search: "",
                          suggestions: [],
                      }
                    : r
            )
        );
    };

    const addRow = () => setRows((prev) => [...prev, newRow()]);
    const removeRow = (key) =>
        setRows((prev) => {
            if (prev.length > 1) {
                return prev.filter((r) => r.key !== key);
            }
            /* Один рядок: «хрестик» очищує форму, як скасувати заповнення. */
            return [newRow()];
        });

    const openEditModal = (d) => {
        const locked = !d.skipMaterialWriteoffs && (d.materialWriteoffs || []).length > 0;
        setEditId(d._id);
        setEditLocked(locked);
        setEditWorkshopId(d.workshopId?._id ? String(d.workshopId._id) : String(d.workshopId || ""));
        setEditDeliveredAt(d.deliveredAt ? formatRangeDateForApi(new Date(d.deliveredAt)) : "");
        setEditNote(d.note || "");
        setEditSkip(!!d.skipMaterialWriteoffs);
        setEditRows(deliveryLinesToEditRows(d.lines));
    };

    const closeEditModal = () => {
        setEditId(null);
        setEditRows([newRow()]);
    };

    const fetchEditSuggestions = async (key, q) => {
        if (!q || q.trim().length < 1) {
            setEditRows((prev) =>
                prev.map((r) => (r.key === key ? { ...r, suggestions: [], searching: false } : r))
            );
            return;
        }
        setEditRows((prev) =>
            prev.map((r) => (r.key === key ? { ...r, searching: true } : r))
        );
        try {
            const variantDbg = isVariantSearchDebugEnabled();
            const res = await axios.get(`${API_PROD}/variants`, {
                params: {
                    q: q.trim(),
                    include_sewing_excluded: "1",
                    ...(variantDbg ? { debug: "1" } : {}),
                },
                withCredentials: true,
            });
            const { offers, serverDebug } = parseProductionVariantSearchResponse(res.data);
            if (variantDbg) {
                console.info("[VARIANT_SEARCH_DEBUG client]", {
                    context: "edit-delivery",
                    q: q.trim(),
                    returnedCount: offers.length,
                    offerIds: offers.map((x) => x.offerId),
                    skus: offers.map((x) => x.sku),
                });
                if (serverDebug) {
                    console.info("[VARIANT_SEARCH_DEBUG server → ProductW stages]", serverDebug);
                }
            }
            setEditRows((prev) =>
                prev.map((r) =>
                    r.key === key ? { ...r, suggestions: offers, searching: false } : r
                )
            );
        } catch {
            setEditRows((prev) =>
                prev.map((r) => (r.key === key ? { ...r, suggestions: [], searching: false } : r))
            );
        }
    };

    useEffect(() => {
        if (!editId) return;
        const timers = {};
        editRows.forEach((r) => {
            if (r.offerId) return;
            const q = String(r.search || "").trim();
            if (q.length < 1) return;
            timers[r.key] = setTimeout(() => void fetchEditSuggestions(r.key, q), 320);
        });
        return () => {
            Object.values(timers).forEach(clearTimeout);
        };
    }, [editId, editRows.map((r) => `${r.key}:${r.search}`).join("|")]);

    const pickEditVariant = (key, v) => {
        setEditRows((prev) =>
            prev.map((r) =>
                r.key === key
                    ? {
                          ...r,
                          offerId: v.offerId,
                          display: v.display,
                          thumbnailUrl: String(v.thumbnailUrl || "").trim(),
                          search: "",
                          suggestions: [],
                      }
                    : r
            )
        );
    };

    const addEditRow = () => setEditRows((prev) => [...prev, newRow()]);
    const removeEditRow = (key) =>
        setEditRows((prev) => {
            if (prev.length > 1) {
                return prev.filter((r) => r.key !== key);
            }
            return [newRow()];
        });

    const saveEditModal = async () => {
        if (!editId) return;
        if (!String(editWorkshopId || "").trim()) {
            setError("Оберіть цех");
            return;
        }
        setSavingEdit(true);
        setError(null);
        try {
            if (editLocked) {
                await axios.patch(
                    `${API_PROD}/deliveries/${editId}`,
                    {
                        workshopId: editWorkshopId,
                        deliveredAt: dateYmdToIsoNoon(editDeliveredAt),
                        note: editNote.trim(),
                    },
                    { withCredentials: true }
                );
            } else {
                const lines = editRows
                    .filter((r) => r.offerId && Number(r.qty) > 0)
                    .map((r) => ({ offerId: r.offerId, quantity: Number(r.qty) }));
                await axios.patch(
                    `${API_PROD}/deliveries/${editId}`,
                    {
                        workshopId: editWorkshopId,
                        deliveredAt: dateYmdToIsoNoon(editDeliveredAt),
                        note: editNote.trim(),
                        lines,
                        skipMaterialWriteoffs: editSkip,
                    },
                    { withCredentials: true }
                );
            }
            closeEditModal();
            await loadAll();
        } catch (e) {
            setError(e.response?.data?.error || e.message || "Не вдалося зберегти");
        } finally {
            setSavingEdit(false);
        }
    };

    const deleteDelivery = async (d) => {
        if (
            !window.confirm(
                "Видалити цю поставку? Якщо було списання матеріалів, залишки на складі буде повернено."
            )
        ) {
            return;
        }
        setDeletingId(d._id);
        setError(null);
        try {
            await axios.delete(`${API_PROD}/deliveries/${d._id}`, { withCredentials: true });
            if (editId === d._id) closeEditModal();
            await loadAll();
        } catch (e) {
            setError(e.response?.data?.error || e.message || "Не вдалося видалити");
        } finally {
            setDeletingId(null);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!String(workshopId || "").trim()) {
            setError("Оберіть цех");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const lines = rows
                .filter((r) => r.offerId && Number(r.qty) > 0)
                .map((r) => ({ offerId: r.offerId, quantity: Number(r.qty) }));
            await axios.post(
                `${API_PROD}/deliveries`,
                {
                    workshopId,
                    deliveredAt: dateYmdToIsoNoon(deliveredAt),
                    note: note.trim(),
                    lines,
                    skipMaterialWriteoffs,
                },
                { withCredentials: true }
            );
            setCreateModalOpen(false);
            resetCreateForm();
            await loadAll();
        } catch (e) {
            setError(e.response?.data?.error || e.message || "Не вдалося зберегти");
        } finally {
            setSaving(false);
        }
    };

    if (loading && !deliveries.length) {
        return <p className="text-muted">Завантаження…</p>;
    }

    const closeCreateModal = () => {
        if (saving) {
            return;
        }
        setCreateModalOpen(false);
        resetCreateForm();
    };

    return (
        <div className="production-tab-panel tab-content">
            <h5 className="mb-3">Поставки з цеху</h5>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <section className="mb-0" aria-label="Список поставок">
                <Form.Label htmlFor="deliveries-filter-q" className="visually-hidden">
                    Пошук поставок
                </Form.Label>
                <div
                    className="d-flex flex-nowrap align-items-stretch gap-2 mb-2"
                    style={{ minWidth: 0 }}
                >
                    <Form.Control
                        id="deliveries-filter-q"
                        className="flex-grow-1"
                        style={{ minWidth: 0 }}
                        value={filterQ}
                        onChange={(e) => setFilterQ(e.target.value)}
                        placeholder="Дата, цех, товар, колір, SKU, коментар, списання матеріалів…"
                        aria-label="Пошук по поставках"
                    />
                    <Button
                        type="button"
                        variant="primary"
                        className="px-3 flex-shrink-0 align-self-stretch"
                        onClick={openCreateModal}
                        title="Нова поставка"
                        aria-label="Нова поставка"
                    >
                        +
                    </Button>
                </div>

                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                    <Button
                        type="button"
                        variant="outline-secondary"
                        size="sm"
                        onClick={goOlderDeliveryWeek}
                        aria-label="Попередній тиждень поставок"
                    >
                        ← Попередній тиждень
                    </Button>
                    <div className="small text-center text-muted flex-grow-1 px-2" style={{ minWidth: "12rem" }}>
                        <span className="text-uppercase text-secondary fw-semibold d-block mb-0" style={{ fontSize: "0.65rem", letterSpacing: "0.04em" }}>
                            Тиждень поставок
                        </span>
                        <span className="text-dark">{formatWeekRangeUk(listWeekStartMonday)}</span>
                    </div>
                    <Button
                        type="button"
                        variant="outline-secondary"
                        size="sm"
                        onClick={goNewerDeliveryWeek}
                        disabled={!canGoNewerDeliveryWeek}
                        aria-label="Наступний тиждень поставок"
                    >
                        Наступний тиждень →
                    </Button>
                </div>

                <div
                    className="materials-hub-table-scroll border rounded"
                    style={{ maxHeight: "min(78vh, 720px)" }}
                >
                    <Table bordered hover size="sm" className="materials-hub-table align-middle mb-0">
                        <thead className="table-light text-center">
                            <tr>
                                <th>Коли</th>
                                <th>Звідки</th>
                                <th className="text-start">Позиції</th>
                                <th>Всього шт.</th>
                                <th className="text-start">Списано матеріалів</th>
                                <th className="text-nowrap" style={{ width: 88 }}>
                                    Дії
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                        {deliveriesInSelectedWeek.map((d) => (
                            <tr key={d._id} className={d.skipMaterialWriteoffs ? "table-warning" : undefined}>
                                <td className="text-nowrap">
                                    {d.deliveredAt
                                        ? new Date(d.deliveredAt).toLocaleDateString("uk-UA")
                                        : "—"}
                                </td>
                                <td>
                                    {d.workshopId?.name || d.sourceLabel || "—"}
                                </td>
                                <td className="text-start small">
                                    <div className="d-flex flex-column gap-1">
                                        {(d.lines || []).map((L) => (
                                            <div key={L._id} className="d-flex align-items-center gap-2">
                                                <VariantThumb
                                                    url={String(L.thumbnailUrl || "").trim()}
                                                    sizePx={28}
                                                />
                                                <div className="min-w-0">
                                                    {L.name}
                                                    {L.colorValue ? (
                                                        <span className="text-muted"> · {L.colorValue}</span>
                                                    ) : null}
                                                    {L.variantLabel ? (
                                                        <span className="text-muted small">
                                                            {" "}
                                                            ({L.variantLabel})
                                                        </span>
                                                    ) : null}{" "}
                                                    — <strong>{L.quantity}</strong> шт.
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </td>
                                <td className="text-center">{d.totalPieces}</td>
                                <td className="text-start small">
                                    {d.skipMaterialWriteoffs ? (
                                        <Badge bg="warning" text="dark" className="fw-normal">
                                            Без списання
                                        </Badge>
                                    ) : (
                                        (d.materialWriteoffs || []).map((m, i) => (
                                            <div key={i}>
                                                {m.typeName} / {m.color}: −{formatQtyHundredths(m.qty)}{" "}
                                                {m.unit}
                                            </div>
                                        ))
                                    )}
                                </td>
                                <td className="text-center text-nowrap">
                                    <Button
                                        type="button"
                                        variant="link"
                                        className="p-0 me-1 text-decoration-none"
                                        style={{ fontSize: "1rem" }}
                                        title="Редагувати"
                                        onClick={() => openEditModal(d)}
                                        disabled={deletingId === d._id}
                                    >
                                        ✏️
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="link"
                                        className="p-0 text-decoration-none"
                                        style={{ fontSize: "1rem", color: "#dc3545" }}
                                        title="Видалити"
                                        onClick={() => void deleteDelivery(d)}
                                        disabled={deletingId === d._id}
                                    >
                                        {deletingId === d._id ? (
                                            <Spinner animation="border" size="sm" />
                                        ) : (
                                            "🗑"
                                        )}
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                        {!loading && deliveries.length > 0 && filteredDeliveries.length > 0 ? (
                            <tfoot className="table-light">
                                <tr>
                                    <td colSpan={3} className="text-end small fw-semibold text-secondary py-2">
                                        Разом за тиждень
                                    </td>
                                    <td className="text-center fw-semibold py-2">{weekTotalPieces}</td>
                                    <td colSpan={2} className="py-2" aria-hidden="true" />
                                </tr>
                                <tr>
                                    <td colSpan={3} className="text-end small fw-semibold text-secondary py-2">
                                        Накат за тиждень (не входить у підсумок)
                                    </td>
                                    <td className="text-center fw-semibold py-2">{weekNakatPieces}</td>
                                    <td colSpan={2} className="py-2" aria-hidden="true" />
                                </tr>
                            </tfoot>
                        ) : null}
                    </Table>
                    {!loading && deliveries.length === 0 && (
                        <p className="text-muted small mb-0 p-2">Поки немає записів.</p>
                    )}
                    {!loading &&
                        deliveries.length > 0 &&
                        filteredDeliveries.length === 0 && (
                            <p className="text-muted small mb-0 p-2">
                                Нічого не знайдено за запитом. Спробуйте інші слова або очистіть поле пошуку.
                            </p>
                        )}
                    {!loading &&
                        deliveries.length > 0 &&
                        filteredDeliveries.length > 0 &&
                        deliveriesInSelectedWeek.length === 0 && (
                            <p className="text-muted small mb-0 p-2">
                                За цим тижнем (з урахуванням пошуку) поставок немає. Перейдіть на інший тиждень
                                кнопками вище або змініть запит у полі пошуку.
                            </p>
                        )}
                </div>
            </section>

            <Modal
                show={createModalOpen}
                onHide={closeCreateModal}
                size="lg"
                centered
                dialogClassName="production-delivery-modal-dialog"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Нова поставка</Modal.Title>
                </Modal.Header>
                <Modal.Body className="production-delivery-modal-body">
                    <Form
                        id="create-delivery-form"
                        className="production-delivery-modal-form"
                        onSubmit={submit}
                    >
                        <div className="production-delivery-modal-form-top">
                            <div className="row g-2 g-md-3 mb-3 align-items-end production-deliveries-meta-row">
                                <div className="col-12 col-md-4">
                                    <Form.Label className="d-block">Дата поставки</Form.Label>
                                    <AppDateField value={deliveredAt} onChange={setDeliveredAt} size="sm" />
                                </div>
                                <div className="col-12 col-md-4">
                                    <Form.Label className="d-block">Цех</Form.Label>
                                    <Form.Select
                                        size="sm"
                                        className="form-select form-select-sm"
                                        value={workshopId}
                                        onChange={(e) => setWorkshopId(e.target.value)}
                                        required
                                    >
                                        <option value="">— оберіть цех —</option>
                                        {workshops.map((w) => (
                                            <option key={w._id} value={w._id}>
                                                {w.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </div>
                                <div className="col-12 col-md-4">
                                    <Form.Label className="d-block">Коментар</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                        placeholder="Необов’язково"
                                    />
                                </div>
                            </div>

                            <Form.Check
                                type="checkbox"
                                id="prod-delivery-skip-writeoffs"
                                className="mb-3 small"
                                checked={skipMaterialWriteoffs}
                                onChange={(e) => setSkipMaterialWriteoffs(e.target.checked)}
                                label="Без списання матеріалів"
                            />
                        </div>

                        <DeliveryLinesEditorTable
                            rows={rows}
                            onPickVariant={pickVariant}
                            onClearOffer={(key) =>
                                setRows((prev) =>
                                    prev.map((x) =>
                                        x.key === key
                                            ? { ...x, offerId: null, display: "", thumbnailUrl: "", search: "" }
                                            : x
                                    )
                                )
                            }
                            onSearchChange={(key, value) =>
                                setRows((prev) =>
                                    prev.map((x) => (x.key === key ? { ...x, search: value } : x))
                                )
                            }
                            onQtyChange={(key, value) =>
                                setRows((prev) =>
                                    prev.map((x) => (x.key === key ? { ...x, qty: value } : x))
                                )
                            }
                            onRemoveRow={removeRow}
                            onAddRow={addRow}
                            compactHeading
                            listZIndex={1060}
                            inModal
                        />
                    </Form>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-end gap-2">
                    <Button type="button" variant="secondary" size="sm" onClick={closeCreateModal} disabled={saving}>
                        Скасувати
                    </Button>
                    <Button type="submit" form="create-delivery-form" variant="primary" size="sm" disabled={saving}>
                        {saving ? "Збереження…" : "Зафіксувати поставку"}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={!!editId}
                onHide={closeEditModal}
                size="lg"
                centered
                dialogClassName="production-delivery-modal-dialog"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Редагувати поставку</Modal.Title>
                </Modal.Header>
                <Modal.Body className="production-delivery-modal-body">
                    {editLocked && (
                        <Alert variant="info" className="py-2 small mb-3">
                            Для цієї поставки вже зафіксовано списання матеріалів. Можна змінити лише{" "}
                            <strong>цех</strong>, <strong>дату</strong> та <strong>коментар</strong>.
                        </Alert>
                    )}
                    <div className="row g-2 g-md-3 mb-3 align-items-end production-deliveries-meta-row">
                        <div className="col-12 col-md-4">
                            <Form.Label className="d-block">Дата поставки</Form.Label>
                            <AppDateField value={editDeliveredAt} onChange={setEditDeliveredAt} size="sm" />
                        </div>
                        <div className="col-12 col-md-4">
                            <Form.Label className="d-block">Цех</Form.Label>
                            <Form.Select
                                size="sm"
                                className="form-select form-select-sm"
                                value={editWorkshopId}
                                onChange={(e) => setEditWorkshopId(e.target.value)}
                                required
                            >
                                <option value="">— оберіть цех —</option>
                                {workshops.map((w) => (
                                    <option key={w._id} value={w._id}>
                                        {w.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                        <div className="col-12 col-md-4">
                            <Form.Label className="d-block">Коментар</Form.Label>
                            <Form.Control
                                size="sm"
                                value={editNote}
                                onChange={(e) => setEditNote(e.target.value)}
                                placeholder="Необов’язково"
                            />
                        </div>
                    </div>

                    {!editLocked && (
                        <div className="production-deliveries-edit-modal-lines">
                            <Form.Check
                                type="checkbox"
                                id="prod-edit-skip-writeoffs"
                                className="mb-3 small flex-shrink-0"
                                checked={editSkip}
                                onChange={(e) => setEditSkip(e.target.checked)}
                                label="Без списання матеріалів"
                            />
                            <DeliveryLinesEditorTable
                                rows={editRows}
                                onPickVariant={pickEditVariant}
                                onClearOffer={(key) =>
                                    setEditRows((prev) =>
                                        prev.map((x) =>
                                            x.key === key
                                                ? { ...x, offerId: null, display: "", thumbnailUrl: "", search: "" }
                                                : x
                                        )
                                    )
                                }
                                onSearchChange={(key, value) =>
                                    setEditRows((prev) =>
                                        prev.map((x) => (x.key === key ? { ...x, search: value } : x))
                                    )
                                }
                                onQtyChange={(key, value) =>
                                    setEditRows((prev) =>
                                        prev.map((x) => (x.key === key ? { ...x, qty: value } : x))
                                    )
                                }
                                onRemoveRow={removeEditRow}
                                onAddRow={addEditRow}
                                compactHeading
                                listZIndex={1060}
                                inModal
                            />
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={closeEditModal} disabled={savingEdit}>
                        Скасувати
                    </Button>
                    <Button variant="primary" size="sm" onClick={() => void saveEditModal()} disabled={savingEdit}>
                        {savingEdit ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-1" /> Збереження…
                            </>
                        ) : (
                            "Зберегти"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
